import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@src/theme/useTheme';
import type { Season } from '@src/theme/season';
import { DEFAULT_GIFT_PRESET, giftPresetId, readGiftPresets, type GiftPreviewPreset } from './giftPreviewPresets';

/** A camera crop of the real game, at the caller's actual viewport dimensions. */
export function GiftHelpPreview({ season, mode, device, preset: customPreset, lazy = false }: {
  season?: Season; mode?: 'light' | 'dark'; device?: 'desktop' | 'mobile'; preset?: GiftPreviewPreset; lazy?: boolean;
} = {}) {
  const { t, i18n } = useTranslation();
  const { seasonPackage, theme } = useTheme();
  const actualSeason = season ?? seasonPackage.id;
  const actualMode = mode ?? theme;
  const frame = useRef<HTMLIFrameElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(!lazy);
  useEffect(() => {
    if (!lazy || !container.current) return;
    const observer = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), { rootMargin: '100px' });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [lazy]);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const view = device ? (device === 'mobile' ? { width: 390, height: 844 } : { width: 1440, height: 900 }) : viewport;
  const preset = customPreset ?? readGiftPresets()[giftPresetId(actualSeason, view.width <= 768, actualMode)] ?? DEFAULT_GIFT_PRESET;
  const [camera, setCamera] = useState({ x: 0, y: 0, scale: 1, width: 420, height: 210, ready: false });
  useEffect(() => {
    const resize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useEffect(() => {
    if (!active) return;
    setCamera(previous => ({ ...previous, ready: false }));
    const requestId = JSON.stringify([active, actualSeason, actualMode, i18n.language, view.width, view.height]);
    const send = () => frame.current?.contentWindow?.postMessage({
      type: 'santa-theme-preview',
      draft: { requestId, theme: actualSeason, mode: actualMode, language: i18n.language,
        name: '', picture: '', logo: null, context: 'profile', showModeToggle: false, giftPreview: true },
    }, window.location.origin);
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === 'santa-theme-preview-ready') send();
      if (event.data?.type !== 'santa-theme-preview-rendered' || event.data.requestId !== requestId) return;
      const bounds = event.data.giftBounds;
      const box = container.current;
      if (!bounds || !box) return;
      // Crop the scene and gift together; never reposition or resize the gift independently.
      const scale = Math.min(1, box.clientWidth / (bounds.width * 3.6));
      const x = Math.max(0, Math.min(view.width * scale - box.clientWidth,
        (bounds.x + bounds.width / 2) * scale - box.clientWidth / 2));
      const y = Math.max(0, (bounds.y + bounds.height) * scale - box.clientHeight * .9);
      setCamera({ x, y, scale, width: box.clientWidth, height: box.clientHeight, ready: true });
      window.clearInterval(retry);
    };
    window.addEventListener('message', receive);
    const retry = window.setInterval(send, 500);
    send();
    return () => { window.clearInterval(retry); window.removeEventListener('message', receive); };
  }, [active, actualSeason, actualMode, i18n.language, view.width, view.height]);
  useEffect(() => {
    if (!camera.ready) return;
    frame.current?.contentWindow?.postMessage({ type: 'santa-gift-preview-adjust', preset }, window.location.origin);
  }, [camera.ready, preset.giftX, preset.giftY, preset.giftScale]);
  return <div ref={container} className="giftee-frame-preview__landscape" aria-busy={!camera.ready}>
    {active && <iframe ref={frame} src={`/theme-preview?theme=${actualSeason}&mode=${actualMode}`} title={t('game.ward.frameHelpPreview')} tabIndex={-1}
      style={{ width: view.width, height: view.height,
        transform: `translate(${-camera.x * preset.zoom + camera.width / 2 * (1 - preset.zoom) + preset.panX}px, ${-camera.y * preset.zoom + camera.height / 2 * (1 - preset.zoom) + preset.panY}px) scale(${camera.scale * preset.zoom})` }} />}
  </div>;
}
